import { createFileRoute, Link } from "@tanstack/react-router";
import { BusinessCaseButton } from "@/components/site/BusinessCaseButton";
import {
  Factory,
  Truck,
  Stethoscope,
  HardHat,
  Briefcase,
  Pickaxe,
  Zap,
  Landmark,
  Building2,
  Users,
  CreditCard,
  Rocket,
  type LucideIcon,
} from "lucide-react";

import howItWorksPhotoSrc from "@/assets/how-it-works-2.png";
const howItWorksPhoto = { url: howItWorksPhotoSrc };
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };
import { Section, SectionHeader } from "@/components/site/Section";
import { EvidenceCallout } from "@/components/site/EvidenceCallout";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { PlainDividedList } from "@/components/site/PlainDividedList";
import { InstantDemo } from "@/components/site/InstantDemo";
import { industries } from "@/content/industries";
import { utilizationIntro, utilizationMethods } from "@/content/utilization";
import { WrapMethodology } from "@/components/site/WrapMethodology";
import { MomentumGrid } from "@/components/site/MomentumGrid";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/solutions")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/solutions/` }],
    meta: [
      { title: "How Leadership Edge Works | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Four steps, thirty minutes a week. You pick the courses; we run them start to finish using the WRAP methodology.",
      },
      { property: "og:title", content: "How Leadership Edge Works" },
      {
        property: "og:description",
        content: "Four steps, thirty minutes a week. Watch, Reflect, Apply, Plan.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: HowItWorks,
});

const wrapSteps = [
  {
    title: "Watch",
    body: "A 30-minute live session once a week, taught by a professional facilitator. High energy, with games, polls, live chat, and rapid-fire discussion with the other participants.",
  },
  {
    title: "Reflect",
    body: "Afterward, participants privately answer one to three reflection questions about the session. It's not a quiz. The point is translating what they heard into their own situation.",
  },
  {
    title: "Apply",
    body: "A bridge assignment carries the content from one week to the next. It might be an essential conversation with a supervisor, a video, a task, or a short one-on-one with an executive coach.",
  },
  {
    title: "Plan",
    body: "At the end of the course, each participant makes a plan for how they'll change their behavior, then tracks the results.",
  },
];

const processSteps: readonly { title: string; body: string }[] = [
  { title: "Enroll for course", body: "Choose a course and a start date from the live schedule." },
  {
    title: "Attend 30-minute session",
    body: "One live session a week, taught by a facilitator, not a slideshow.",
  },
  {
    title: "Complete weekly application assignments",
    body: "Short assignments that put the week's skill to work on the job.",
  },
  {
    title: "Make an Action Plan per course",
    body: "Each participant writes down what they'll change, then tracks it.",
  },
];

const ongoingSupport = {
  title: "Learn and grow with support from a Development Pro",
  body: "A real person checks in, answers questions, and keeps momentum going.",
};

const gettingStarted = [
  {
    icon: Users,
    title: "Determine who should have access",
    body: "Decide which managers and rising leaders need it first.",
  },
  {
    icon: CreditCard,
    title: "Pay for their access level",
    body: "Pick the access level that fits your team and budget.",
  },
  {
    icon: Rocket,
    title: "Start learning and growing",
    body: "They register, join a session, and get to work.",
  },
];

const industryIcons: Record<string, LucideIcon> = {
  "small-midsize-businesses": Building2,
  manufacturing: Factory,
  "supply-chain-logistics": Truck,
  healthcare: Stethoscope,
  "construction-engineering": HardHat,
  "professional-business-services": Briefcase,
  "mining-natural-resources": Pickaxe,
  "utilities-energy": Zap,
  "government-public-sector": Landmark,
};

const industryHooks: Record<string, string> = {
  "small-midsize-businesses": "No HR department, no problem",
  manufacturing: "Your best welder now runs the line",
  "supply-chain-logistics": "Turnover on the dock, every quarter",
  healthcare: "Charge nurses leading their old peers",
  "construction-engineering": "Foremen promoted for their hands",
  "professional-business-services": "Top billers managing people now",
  "mining-natural-resources": "Safety depends on how crews talk",
  "utilities-energy": "Retiring crews, new supervisors",
  "government-public-sector": "Lead well without a bigger budget",
};

function titleCase(value: string) {
  const small = new Set(["and", "or", "the", "of"]);
  return value
    .toLowerCase()
    .split(" ")
    .map((w) => (small.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

function HowItWorks() {
  return (
    <>
      <section className="relative isolate overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <img
            src={howItWorksPhoto.url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>
        <div className="relative mx-auto flex min-h-[620px] max-w-6xl items-center px-6 py-16 md:py-20">
          <div className="max-w-[600px]">
            <h1
              className="font-sans text-4xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Sixty minutes a week.
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              You choose the courses and who attends. We run everything else, start to finish.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/course-catalog/"
                className="inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
              >
                View the Course Catalog
              </Link>
              <Link
                to="/contact/"
                className="inline-flex items-center justify-center rounded-[6px] border border-black/40 bg-transparent px-6 py-3 text-sm font-semibold text-black transition-colors hover:border-black/60 hover:bg-black/5"
              >
                Talk to a Development Pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Process map + video */}
      <Section tone="default">
        <SectionHeader
          eyebrow="The process"
          title="What it looks like, step by step"
          intro="These steps repeat for every course."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />
        <div>
          <div>
            <ol className="relative space-y-6">
              {/* connector line running through the numbered markers */}
              <span
                className="absolute left-5 top-3 bottom-3 w-px bg-primary/25"
                aria-hidden="true"
              />
              {processSteps.map((step, i) => (
                <li key={step.title} className="relative flex gap-5">
                  <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#693D94] font-display text-sm font-semibold text-white">
                    {i + 1}
                  </span>
                  <div className="pt-1">
                    <h3
                      style={{ fontFamily: "'Poppins', sans-serif" }}
                      className="text-base font-semibold text-ink"
                    >
                      {step.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {step.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-8 rounded-2xl border border-border/60 bg-background p-7">
              <h3
                style={{ fontFamily: "'Poppins', sans-serif" }}
                className="text-base font-semibold text-ink"
              >
                {ongoingSupport.title}
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {ongoingSupport.body}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-12 md:mt-16">
          <InstantDemo />
        </div>
      </Section>

      {/* 3. WRAP */}
      <Section tone="lavender">
        <SectionHeader
          eyebrow="Inside every session, the same four moves"
          title="The WRAP methodology"
          intro="A 30-minute session each week, focused on immediate workplace application."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />
        <WrapMethodology steps={wrapSteps} />
      </Section>

      {/* 4. Getting your team started */}
      <Section tone="default">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            Getting your team started
          </p>
          <h2
            className="mt-3 text-3xl font-semibold leading-tight text-ink md:text-4xl"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Three steps to get access
          </h2>
          <div className="mt-10">
            <PlainDividedList items={gettingStarted} />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/purchase/"
              className="inline-flex shrink-0 items-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
            >
              See pricing
            </Link>
            <BusinessCaseButton />
          </div>
        </div>
      </Section>

      {/* 5. Industries */}
      <Section tone="lavender">
        <SectionHeader
          eyebrow="Who we help"
          title="See how Leadership Edge Live works in different industries"
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />
        <div className="grid gap-x-12 md:grid-cols-2">
          {[industries.slice(0, 5), industries.slice(5)].map((column, ci) => (
            <div key={ci}>
              {column.map((industry) => {
                const Icon = industryIcons[industry.slug] ?? Building2;
                const name = titleCase(industry.eyebrow);
                return (
                  <Link
                    key={industry.slug}
                    to="/who-we-help/$slug/"
                    params={{ slug: industry.slug }}
                    className="group flex items-center gap-4 border-b border-border/60 py-4"
                  >
                    <Icon
                      className="h-10 w-10 shrink-0 text-ink"
                      strokeWidth={1.5}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <h3
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                        className="text-base font-semibold text-ink"
                      >
                        {name}
                      </h3>
                      <p className="text-sm leading-snug text-muted-foreground">
                        {industryHooks[industry.slug] ?? industry.supporting}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-[6px] border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition-colors group-hover:bg-primary/10">
                      View
                    </span>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </Section>

      {/* 6. Utilization methods */}
      <Section tone="default">
        <SectionHeader
          eyebrow="Ways to drive learning and engagement"
          title="Fourteen optional ways to build momentum"
          intro={utilizationIntro}
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />
        <MomentumGrid methods={utilizationMethods} />
      </Section>

      {/* 7. Research + closing CTA */}
      <Section tone="lavender">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              The research behind the approach
            </p>
            <h2
              className="mt-3 text-3xl font-semibold leading-tight text-ink md:text-4xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Development principles supported by leadership-training research
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              A meta-analysis of 335 leadership-training samples (Lacerenza et al., 2017) found
              positive effects on learning, on-the-job behavior, job performance, and organizational
              results. The strongest results came from training with feedback, practice, and
              sessions spaced over time.
            </p>
          </div>
          <EvidenceCallout
            quote="Effective leadership training combines needs analysis, feedback, practice, and sessions spaced over time."
            source="Lacerenza et al., Journal of Applied Psychology (2017)"
            href="https://pubmed.ncbi.nlm.nih.gov/28749153/"
          />
        </div>
      </Section>

      <AdvisorCTA
        backgroundImage={boardroomAsset.url}
        title="Ready to see the catalog?"
        body="Browse every live course, or talk to a Development Pro about where to start."
      />
    </>
  );
}
